import get from 'lodash/get';

const fontSizes = [12, 14, 16, 18, 20, 24, 32, 38, 40, 48, 56, 62, 70];
const space = [0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60];

const shadows = {
  tiny: '0px 1px 2px #0000001a',
  small: '0px 3px 6px #00000029',
  large: '0px 8px 32px rgba(15, 23, 42, 0.08)',
  smallShadow:
    '0px 4px 10px -2px rgba(16, 24, 40, 0.08), 0px 4px 5px -3px rgba(16, 24, 40, 0.03)',
  largeShadow: '0px 24px 48px -12px rgba(16,25,40,0.18)'
};

export const th = (path) => (props) => get(props.theme, path);

const getValue = (props, variant) =>
  typeof variant === 'function' ? variant(props) : variant;

// Creates a function that returns a matching prop value when passed a string
export const variants = (prop, values) => (props) => {
  const variant = props[prop] && values[props[prop]];
  return variant && getValue(props, variant);
};

// All the code that controls vertical stacking is in one place. The idea being
// that you could slip something in between later if need be, which you couldn't
// if you did 1, 2, 3, etc, because z-index doesn't support decimals.
export const zIndexes = {
  notification: 9000,
  overlay: 8000,
  modal: 7000,
  navigation: 6000,
  dropdown: 5000
};

export const colors = {
  trueBlack: '#000',
  black: '#273C49',
  'pickledBluewood-1': '#1F303A',
  lightBlack: '#385264',
  fiord: '#3B515F',
  white: '#FFFFFF',
  transparentWhite75: 'rgba(255, 255, 255, 0.75)',
  fountain200: '#BFE8E9',
  lime500: '#D1C627',

  pickledBluewood45: '#FAFAFB',
  pickledBluewood4: '#f4f5f6',
  pickledBluewood35: '#E9ECED',
  pickledBluewood3: '#A9B1B6',
  pickledBluewood2: '#7D8A92',
  pickledBluewood1: '#52636D',
  pickledBluewood: '#273C49',
  'pickledBluewood-2': '#17242C',
  primaryText: '#FFFFFF',
  opacityBlue: '#e0f4f4',
  turquoise: '#5CC6C7',
  fadedTurquoise: '#7eb1b7',
  cyan: '#71DCDD',
  mint: '#B8F1F1',
  iceCold: '#B2F8F8',
  jaggedIce: '#C2EAEA',
  powderBlue: '#BEE8E9',
  dew: '#EAFFFF',
  darkDew: '#107B7C',
  darkTurquoise: '#109D9F',
  darkFountain: '#3BAAAB',
  azureWhite: '#DEF4F4',
  fountainBlue: '#5CC6C7',
  'fountainBlue-1': '#45BEBF',
  'fountainBlue-2': '#3BAAAB',
  'fountainBlue-3': '#339394',
  'fountainBlue-4': '#2B7D7D',
  'fountainBlue+1': '#7DD1D2',
  'fountainBlue+2': '#BEE8E9',
  'fountainBlue+3': '#DEF4F4',
  'fountainBlue+4': '#EFF9F9',

  porsche: '#EDAD68',
  'porsche+4': '#FBEDDE',
  'porsche-2': '#E68F31',
  'porsche-4': '#C37117',
  'porsche-weak': '#fde7d5',

  deepBlush: '#E1718B',
  'deepBlush+4': '#FFF9FA',
  'deepBlush-2': '#D63E62',
  'deepBlush-4': '#B22646',

  // blues
  blue: '#50AEEB',
  lightBlueGray: '#77A6E2',
  blueGray: '#6A9AD6',
  blueGray2: '#CFD5DD',
  blueGray3: '#64748B',
  blueGray4: '#73839a',
  blueGray5: `#C7C7ff`,
  steelBlue: '#477ab9',
  aliceBlue: '#EBF4FF',
  pictonBlue: '#5B8DCB',
  pictonBlue0: '#50AEEB',
  'pictonBlue+45': '#F5FBFF',
  'pictonBlue+4': '#E3F2FC',
  'pictonBlue+2': '#87C7F1',
  'pictonBlue+1': '#6BBBEE',
  'pictonBlue-2': '#1A94E4',
  'pictonBlue-3': '#1782C8',
  'pictonBlue-4': '#1471AD',
  darkPictonBlue: '#2498E5',
  chambrayBlue: '#35587D',
  cornflowerBlue: '#84ade0',
  regentBlue: '#99bae2',
  frenchBlue: '#BDDBFF',
  danubeBlue: '#5B8DCB',
  jordyBlue: '#77A6E2',
  indigoBlue: '#4982C9',
  blueBayoux: '#4c6372',
  fadedBlue: '#87B1E5',
  fountainBlueLight: '#45bebf',

  'sushi-1': '#5A9A2E',
  sushi: '#71C13A',
  'sushi+4': '#E3F3D8',

  'confetti-4': '#C89B04',
  'confetti-2': '#E6BC31',
  confetti: '#EDCE68',
  'confetti+1': '#F1D886',
  'confetti+2': '#F4E2A4',
  'confetti+4': '#FBF5E1',

  keppelBlue: '#0c8b8b',

  'fuzzyWuzzyBrown+4': '#F6E7E5',
  fuzzyWuzzyBrown: '#C76358',
  'fuzzyWuzzyBrown-2': '#A94439',

  // pink, red, nude and orange
  nude: '#EFDAC4',
  mutedOrange: '#EFC89F',
  orange: '#EDAD68',
  darkOrange: '#DB9D5A',

  red: '#C76358',
  lightRed: '#fef2f2',
  darkRed: '#B75B51',
  mutedRed: '#F5988D',
  darkRed4: '#601B1B',
  lightRed4: '#FCDADA',
  quietPink: '#dda19b',

  pink: '#E1718B',
  lightPink: '#FFEDF1',
  mediumPink: '#F298AE',
  darkPink: '#DB6580',

  // yellow
  yellow: '#EDCE68',
  lightYellow: '#ffe9b8',
  paleYellow: '#F8EFD0',

  // purples
  minskLight: '#4545A2',
  minsk: '#333377',
  minskDark: '#21214C',

  indigoLigth4: '#E1E1F4',
  indigoLigth3: '#B7B7E5',
  indigoLigth2: '#8383D2',
  indigoLigth1: '#6C6CCA',
  indigo: '#5656C2',
  indigoDark1: '#4242B7',
  indigoDark2: '#3A3AA1',
  indigoDark3: '#32328A',
  indigoDark4: '#2A2A74',

  blueVioletLight: '#6C6CC9',
  blueViolet: '#4343B5',
  blueVioletDark: '#323288',

  portGoreLight: '#353586',
  portGore: '#24245A',
  portGoreDark: '#13132E',

  // greens
  mintGreen: '#93FFB3',
  bermudaGreen: '#7ADDB6',
  chateauGreen: '#3FB254',
  charlotte: '#B2F8F8',

  bermudaLight4: '#E3F8F0',
  bermudaLight3: '#C7F0E0',
  bermudaLight2: '#ABE9D1',
  bermudaLight1: '#92E3C3',
  bermuda: '#7ADDB6',
  bermudaDark1: '#62D7A9',
  bermudaDark2: '#49D19B',
  bermudaDark3: '#33C88D',
  bermudaDark4: '#2DB07C',

  // grays
  galleryGray: '#F0F0F0',
  silverGray: '#C4C4C4',
  silverLightGray: '#BEBEBE',
  silverChaliceGray: '#A2A2A2',
  doveGray: '#707070',
  regentGray: '#939EA4',
  darkSlateGray: '#364c5a',
  osloGray: '#7D8A92',
  blackCoral: '#52636D',

  transparentWhite: [...new Array(10).keys()].map(
    // eg. transparentWhite.9 has opacity 0.9
    (i) => `rgba(255, 255, 255, ${i / 10})`
  ),
  transparentBlack: [...new Array(10).keys()].map(
    // eg. transparentBlack.9 has opacity 0.9
    (i) => `rgba(39, 60, 73, ${i / 10})`
  )
};

// global button styles
colors.darkButton = {
  default: colors.black,
  hover: '#385264',
  active: '#627886',
  text: colors.white
};

colors.flatButton = {
  hover: '#E1E1F4'
};

colors.dangerButton = {
  default: colors.red,
  hover: '#E07266',
  active: '#EA8C81',
  text: colors.white
};

colors.successButton = {
  default: colors.turquoise,
  hover: '#85E2E3',
  active: colors.iceCold,
  text: colors.black
};

colors.disabledButton = {
  default: colors.silverGray,
  hover: colors.silverGray,
  active: colors.silverGray,
  text: colors.black
};

colors.primaryButton = {
  default: colors.turquoise,
  hover: '#85E2E3',
  active: colors.iceCold,
  text: colors.black
};

// links
colors.darkLink = {
  default: colors.black,
  hover: colors.white
};

colors.lightLink = {
  default: colors.white,
  hover: colors.black
};

colors.primaryLink = {
  default: colors.turquoise,
  hover: '#85E2E3'
};

// modal
colors.modalBg = { primary: colors.white };
colors.modalHeaderBg = colors.white;
colors.modalText = colors.black;
colors.backdrop = 'rgba(39, 60, 73, 0.9)';

const fonts = {
  headings: 'Nimbus, serif',
  body: 'Inter, sans-serif',
  paragraphSpacing: 'initial'
};

const theme = {
  colors,
  fonts,
  shadows,
  space,
  fontSizes,
  zIndexes
};

export default theme;
